import { FormField } from "@/components/forms/FormField.tsx";
import { FormMaxWidthWrapper } from "@/components/forms/FormMaxWidthWrapper.tsx";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import TF from "@/components/forms/TextField.module.css";
import { WNumberField } from "@/components/forms/WNumberField.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { getSizeParams } from "@/modules/compute/commonViewModel.ts";
import { getDefaultDesignatedPoolForLocation } from "@/modules/compute/pool/pool.ts";
import { useSizeSelect } from "@/modules/compute/useSizeSelect.ts";
import type { VmSizePackage } from "@/modules/compute/VmSize.types.ts";
import { VmSizeSelect } from "@/modules/compute/VmSizeSelect.tsx";
import type { SizeValue } from "@/modules/compute/vmSizeSelectUtils.ts";
import { canCreateResourceHostPoolsAtom } from "@/modules/pools/hostPoolsQuery.ts";
import { asGibiBytes } from "@/modules/services/kubernetes/utils.ts";
import { allServiceImagesByNameAtom } from "@/modules/services/serviceImagesQuery.ts";
import { updateServicePropertiesMutation } from "@/modules/services/servicesQuery.ts";
import { cn } from "@/utils/classNames.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useLingui } from "@lingui/react/macro";
import type {
    KubernetesProviderWorker,
    ManagedKubernetesService,
    ServiceImage,
} from "@warrenio/api-spec/spec.oats.gen";
import { mustGet } from "@warrenio/utils/collections/mustGet";
import { notNull } from "@warrenio/utils/notNull";
import { type ReactNode, useState } from "react";
import { Input } from "react-aria-components";
import type { Promisable } from "type-fest";

export interface NodeResizeModalProps {
    node?: KubernetesProviderWorker;
    service: ManagedKubernetesService;
    index?: number;
    button: ReactNode;
}

export interface NodeResizeInputs {
    size: SizeValue;
    autoScalerMin: number;
    autoScalerMax: number;
    maxSurge: number;
    name: string;
}

export function NodeResizeButton(props: NodeResizeModalProps) {
    return (
        <WModal button={props.button}>
            <NodeResizeModalContent {...props} />
        </WModal>
    );
}

function NodeResizeModalContent(props: NodeResizeModalProps) {
    //#region Hooks
    const mutation = useStandardMutation(updateServicePropertiesMutation);
    const { service, index, node } = props;
    const { properties, uuid } = service;
    //#endregion

    const providerWorkers = properties?.spec?.provider?.workers ?? [];

    return (
        <BaseResizeModal
            node={node}
            nodes={providerWorkers}
            index={index}
            location={properties.location}
            onResize={async (body) => {
                const workers = [...providerWorkers];
                if (typeof index === "number" && Number.isInteger(index)) {
                    workers.splice(index, 1);
                }

                const node = {
                    machine: {
                        vcpu: body.size.vcpu,
                        ram: body.size.ram,
                    },
                    name: body.name,
                    minimum: body.autoScalerMin,
                    maximum: body.autoScalerMax,
                    maxSurge: body.maxSurge,
                    volume: {
                        size: asGibiBytes(body.size.disks),
                    },
                };

                const provider = {
                    workers: [...workers, node],
                };

                const maintenance = {
                    ...notNull(properties?.spec?.maintenance),
                };
                await mutation.mutateAsync({ uuid: uuid, body: { maintenance, provider } });
            }}
        />
    );
}

interface BaseResizeModalProps {
    node?: KubernetesProviderWorker;
    nodes: KubernetesProviderWorker[];
    index?: number;
    location: string;
    onResize: (body: NodeResizeInputs) => Promisable<void>;
}

export function BaseResizeModal({ node, nodes, index, location, onResize }: BaseResizeModalProps) {
    const { t } = useLingui();
    const pools = useSuspenseQueryAtom(canCreateResourceHostPoolsAtom);
    const allImages = useSuspenseQueryAtom(allServiceImagesByNameAtom);
    const service: ServiceImage = mustGet(allImages, "kubernetes", "Service OS");
    const [name, setName] = useState<string>(
        () => node?.name ?? `worker-${(Math.random() + 1).toString(36).substring(7)}`,
    );
    const [autoScalerMin, setAutoScalerMin] = useState<number>(node?.minimum ?? 1);
    const [autoScalerMax, setAutoScalerMax] = useState<number>(node?.maximum ?? 5);
    const [maxSurge, setMaxSurge] = useState<number>(node?.maxSurge ?? 1);
    const [footerNotice, setFooterNotice] = useState<string | undefined>(undefined);

    const pool = getDefaultDesignatedPoolForLocation(pools, location);
    const sizeParams = getSizeParams(pool?.guest_limits);

    const packages =
        service?.specification?.provider.workers.packages.map(
            (obj): VmSizePackage => ({
                ...obj,
                ram: obj.ram * 1024,
            }),
        ) ?? [];

    const defaultPackage = packages[0];

    const { machine, volume } = node ?? {};
    const primarySize = volume?.size ? parseInt(volume.size, 10) : defaultPackage.ssd;
    const { size, props } = useSizeSelect(
        {
            disks: notNull(primarySize),
            vcpu: notNull(machine?.vcpu ?? defaultPackage.cpu),
            ram: notNull(machine?.ram ?? defaultPackage.ram),
            isCustom: false,
        },
        { os_name: "", os_version: "" },
    );

    const nodeData = {
        os_name: "",
        os_version: "",
        location: location,
    };

    function updateAutoScalerMin(value: number) {
        setAutoScalerMin(value);

        const workers = [...nodes];
        if (typeof index === "number" && Number.isInteger(index)) {
            workers.splice(index, 1);
        }

        if (!workers.find((item: KubernetesProviderWorker) => item.minimum > 0) && value === 0) {
            setFooterNotice(t`At least one node pool must have autoscaler minimum value as 1 or more.`);
        } else {
            setFooterNotice(undefined);
        }
    }

    async function onSubmit() {
        await onResize({
            autoScalerMin,
            autoScalerMax,
            maxSurge,
            name,
            size,
        });
    }

    return (
        <WModalContent
            title={node ? t`Edit node` : t`Add node`}
            label={node ? t`Edit` : t`Add`}
            footerNotice={footerNotice}
            isActionDisabled={!!footerNotice}
            modalAction={onSubmit}
        >
            <FormMaxWidthWrapper>
                <FormField wide label={t`Size`} block isRequired>
                    <RadioGroupDiv>
                        <VmSizeSelect
                            hideCustom
                            vmData={nodeData}
                            {...props}
                            packages={packages}
                            sizeParams={sizeParams}
                        />
                    </RadioGroupDiv>
                </FormField>

                <RadioGroupDiv>
                    <WNumberField
                        label={t`Autoscaler min.`}
                        headless
                        block
                        value={autoScalerMin}
                        onChange={(value) => updateAutoScalerMin(value)}
                        className={cn(TF.NumberField)}
                        aria-label="Autoscaler min."
                        isRequired
                        minValue={0}
                        formatOptions={{
                            maximumFractionDigits: 0,
                            signDisplay: "never",
                            useGrouping: false,
                        }}
                    >
                        <Input className={TF.Input} />
                    </WNumberField>

                    <WNumberField
                        label={t`Autoscaler max.`}
                        headless
                        block
                        value={autoScalerMax}
                        onChange={(value) => setAutoScalerMax(value)}
                        className={cn(TF.NumberField, "mt-0!")}
                        aria-label="Autoscaler max."
                        isRequired
                        minValue={0}
                        formatOptions={{
                            maximumFractionDigits: 0,
                            signDisplay: "never",
                            useGrouping: false,
                        }}
                    >
                        <Input className={TF.Input} />
                    </WNumberField>

                    <WNumberField
                        label={t`Max. surge`}
                        headless
                        block
                        value={maxSurge}
                        onChange={(value) => setMaxSurge(value)}
                        className={cn(TF.NumberField, "mt-0!")}
                        aria-label="Max. surge"
                        isRequired
                        minValue={0}
                        formatOptions={{
                            maximumFractionDigits: 0,
                            signDisplay: "never",
                            useGrouping: false,
                        }}
                    >
                        <Input className={TF.Input} />
                    </WNumberField>
                </RadioGroupDiv>

                <WTextField block isRequired label={t`Name`} value={name} onChange={setName}>
                    <Input className={TF.Input} />
                </WTextField>
            </FormMaxWidthWrapper>
        </WModalContent>
    );
}
